import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'

export type AdjustmentKind = 'vpi' | 'manufacturer' | 'manual'
export type AdjustmentStatus = 'preview' | 'applied' | 'reverted'
export type AdjustmentScopeType = 'all' | 'tariff' | 'product' | 'tariff_component'

export interface PriceAdjustmentRow {
  id: string
  tenantId: string
  kind: AdjustmentKind
  name: string
  factorBp: number
  effectiveDate: string
  status: AdjustmentStatus
  scopeType: AdjustmentScopeType
  scopeId: string | null
  previewSummary: PriceAdjustmentSummary | null
  appliedSummary: PriceAdjustmentSummary | null
  notes: string | null
  createdAt: string
  appliedAt: string | null
  revertedAt: string | null
}

export interface PriceAdjustmentSummary {
  affectedComponents: number
  affectedTiers: number
  affectedContracts: number
  totalRevenueChangeCents: number
  triggersSpecialTermination: boolean
  contractIdsTriggeringSpecialTermination: string[]
  components?: Array<{
    componentId: string
    oldUnitPriceCents: number
    newUnitPriceCents: number
    oldUnitCostCents: number | null
    newUnitCostCents: number | null
    tierChanges: Array<{ tierId: string; oldUnitPriceCents: number; newUnitPriceCents: number }>
  }>
  specialTerminationUntil?: string | null
}

export interface CreatePriceAdjustmentInput {
  kind: AdjustmentKind
  name: string
  factorBp: number
  effectiveDate: string
  scopeType: AdjustmentScopeType
  scopeId?: string | null
  notes?: string | null
}

export function usePriceAdjustments(status?: AdjustmentStatus) {
  return useQuery<PriceAdjustmentRow[]>({
    queryKey: ['price-adjustments', 'list', status ?? 'all'],
    queryFn: () =>
      apiClient
        .get('/price-adjustments', { params: status ? { status } : {} })
        .then((r) => r.data),
  })
}

export function usePriceAdjustment(id: string | undefined) {
  return useQuery<PriceAdjustmentRow>({
    queryKey: ['price-adjustments', 'detail', id],
    queryFn: () => apiClient.get(`/price-adjustments/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

export function useCreatePriceAdjustmentPreview() {
  const qc = useQueryClient()
  return useMutation<{ id: string; preview: PriceAdjustmentSummary }, Error, CreatePriceAdjustmentInput>({
    mutationFn: (input) => apiClient.post('/price-adjustments/preview', input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['price-adjustments'] }),
  })
}

export function useApplyPriceAdjustment() {
  const qc = useQueryClient()
  return useMutation<{ applied: PriceAdjustmentSummary }, Error, string>({
    mutationFn: (id) => apiClient.post(`/price-adjustments/${id}/apply`, {}).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['price-adjustments'] })
      void qc.invalidateQueries({ queryKey: ['contracts'] })
      void qc.invalidateQueries({ queryKey: ['tariffs'] })
    },
  })
}

export function useRevertPriceAdjustment() {
  const qc = useQueryClient()
  return useMutation<{ reverted: number }, Error, string>({
    mutationFn: (id) => apiClient.post(`/price-adjustments/${id}/revert`, {}).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['price-adjustments'] })
      void qc.invalidateQueries({ queryKey: ['contracts'] })
      void qc.invalidateQueries({ queryKey: ['tariffs'] })
    },
  })
}

export function useDeletePriceAdjustment() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/price-adjustments/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['price-adjustments'] }),
  })
}
